import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService, rememberRecordOutlets } from 'api/services/dineOpsService';
import type { DineAwaitingBill, DineBillSummary, DineBillingPoint, DineShift } from 'api/types';
import DineOpsSettleModal from 'components/modals/modules/dineops-module/DineOpsSettleModal';
import DineOpsNewBillModal from 'components/modals/modules/dineops-module/DineOpsNewBillModal';
import DineOpsMoveBillModal from 'components/modals/modules/dineops-module/DineOpsMoveBillModal';
import { printBill } from './billPrint';
import { PrintAlertBanner, canSeeModule, currentOutletId, PRINT_MODULES } from 'components/modules/print-module/printUi';

/**
 * BILL-003 — the cashier's queue (cycle 4).
 *
 * Two things in one list: tables that asked for a bill and have none yet
 * (the top rows, "Generate"), and bills — open ones first. The waiter records
 * who is at the table; the cashier decides how it settles. A row flagged
 * in-house is information, not a decision: every bill starts WALK_IN.
 *
 * Billing points (DO-313, spec 2.40 §12.1a): a bill goes to its table's
 * section's counter. A cashier's queue is the counter their shift is at; a user
 * with the move-bill right (a manager) may look at any counter or all of them,
 * and move an unpaid bill to another counter with a reason.
 *
 * Shared counter (S1): a counter can serve several outlets; its queue lists the
 * bills of all of them with an Outlet column, and every action on a bill goes
 * to the bill's own outlet (rememberRecordOutlets) without a header switch.
 * Every receipt needs an open shift at a counter serving the bill's outlet, so
 * the screen offers Open a shift… in place when there is none.
 */

/** One flat row for the grid: a waiting table or a bill. Optional fields belong to one kind. */
interface QueueRow {
  shareLabel?: string;
  kind: 'awaiting' | 'bill';
  id: string;
  orderId: string;
  orderNumber: string;
  tableNumber?: string;
  roomNumber?: string;
  guestPresence: 'WALK_IN' | 'IN_HOUSE';
  guestName?: string;
  isVip: boolean;
  covers: number;
  billingPointName?: string;
  outletName?: string | null;
  // bill
  billNumber?: string;
  billStatus?: 'GENERATED' | 'SETTLED' | 'VOID';
  settlementType?: string;
  totalAmount?: number;
  paidAmount?: number;
  balanceDue?: number;
  generatedAt?: string;
  // awaiting
  subtotal?: number;
  billRequestedAt?: string;
  waitingMinutes?: number;
  unsentCount?: number;
  unservedKots?: number;
  canBill?: boolean;
}

const toRow = (x: DineAwaitingBill | DineBillSummary): QueueRow =>
  'billNumber' in x
    ? { kind: 'bill', id: x.id, orderId: x.orderId, orderNumber: x.orderNumber, tableNumber: x.tableNumber, roomNumber: x.roomNumber, guestPresence: x.guestPresence, guestName: x.guestName, isVip: x.isVip, covers: x.covers,
        shareLabel: x.shareLabel, billingPointName: x.billingPointName, outletName: x.outletName,
        billNumber: x.billNumber, billStatus: x.billStatus, settlementType: x.settlementType, totalAmount: x.totalAmount, paidAmount: x.paidAmount, balanceDue: x.balanceDue, generatedAt: x.generatedAt }
    : { kind: 'awaiting', id: `await-${x.orderId}`, orderId: x.orderId, orderNumber: x.orderNumber, tableNumber: x.tableNumber, roomNumber: x.roomNumber, guestPresence: x.guestPresence, guestName: x.guestName, isVip: x.isVip, covers: x.covers,
        billingPointName: x.billingPointName, outletName: x.outletName, subtotal: x.subtotal, billRequestedAt: x.billRequestedAt, waitingMinutes: x.waitingMinutes, unsentCount: x.unsentCount, unservedKots: x.unservedKots, canBill: x.canBill };

/** counter: '' = my counter (my shift's), 'all' = every counter, else a billing point id. */
interface Filters { status?: string; counter?: string }

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DineOpsBillQueueScreen: React.FC<{ allowedActions?: string[]; onNavigateModule?: (moduleId: string) => void }> = ({ allowedActions = [], onNavigateModule }) => {
  const canMove = allowedActions.includes('move-bill');
  const canVoid = allowedActions.includes('void');
  const [myShift, setMyShift] = useState<DineShift | null>(null);
  const [counters, setCounters] = useState<DineBillingPoint[]>([]);
  const [moving, setMoving] = useState<DineBillSummary | null>(null);
  const [awaiting, setAwaiting] = useState<DineAwaitingBill[]>([]);
  const [bills, setBills] = useState<DineBillSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  /** QUICK VIEWS chip (as on Bookings): one tap narrows the queue; Clear goes back to everything open. */
  const [quick, setQuick] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({ status: 'open' });
  const [settling, setSettling] = useState<string | null>(null);
  const [newBill, setNewBill] = useState(false);
  /** The New bill picker's list: requested tables plus served orders that have not asked yet. */
  const [billable, setBillable] = useState<DineAwaitingBill[]>([]);
  useEffect(() => {
    if (!newBill) return;
    let alive = true;
    dineOpsService.getAwaitingBills(counterParam, true).then((a) => { rememberRecordOutlets(a); if (alive) setBillable(a); }).catch(() => { if (alive) setBillable(awaiting); });
    return () => { alive = false; };
  }, [newBill]); // eslint-disable-line react-hooks/exhaustive-deps
  const [voiding, setVoiding] = useState<DineBillSummary | null>(null);
  const [voidReason, setVoidReason] = useState('');
  /** Open a shift… in place (D6: every receipt needs one). */
  const [opening, setOpening] = useState(false);
  const [openCounter, setOpenCounter] = useState('');
  const [openFloat, setOpenFloat] = useState('');
  const timer = useRef<number | null>(null);

  // Which counter the queue shows (spec 2.41 §12.1a). Without the move-bill right: the caller's
  // shift's counter, and nothing without a shift, so one counter never sees another's bills.
  // A manager may pick any counter or all; with no pick and no shift of their own they see all.
  const counterParam = useMemo(() => {
    if (!canMove) return undefined;
    if (!filters.counter) return myShift ? undefined : { all: true };
    return filters.counter === 'all' ? { all: true } : { billingPointId: filters.counter };
  }, [canMove, filters.counter, myShift]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const status = (filters.status || 'open') as 'open' | 'settled' | 'void' | 'all';
      const [a, b, shift, bp] = await Promise.all([
        dineOpsService.getAwaitingBills(counterParam), dineOpsService.getBills(status, undefined, counterParam),
        dineOpsService.getCurrentShift().catch(() => null), dineOpsService.getBillingPoints().catch(() => [] as DineBillingPoint[]),
      ]);
      rememberRecordOutlets(a); rememberRecordOutlets(b);
      setAwaiting(a); setBills(b); setMyShift(shift); setCounters(bp);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [filters.status, counterParam]);

  const noShift = !myShift && !canMove;
  const myCounter = myShift?.billingPointName
    ? `${myShift.billingPointName}${myShift.outletName ? `, ${myShift.outletName}` : ''}` : '';
  const showing = !filters.counter || !canMove
    ? (myCounter ? `${myCounter} (your shift)` : canMove ? 'All counters' : 'No counter: open a shift at your counter to see its bills')
    : filters.counter === 'all' ? 'All counters' : counters.find((c) => c.id === filters.counter)?.billingPointName ?? 'a counter';

  useEffect(() => {
    void load();
    // The queue is live: a waiter asks for a bill and it should appear without a click.
    timer.current = window.setInterval(() => { void load(); }, 15_000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load]);

  const rows = useMemo<QueueRow[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    const showAwaiting = !filters.status || filters.status === 'open' || filters.status === 'all';
    const a: QueueRow[] = showAwaiting ? awaiting.map(toRow) : [];
    const b: QueueRow[] = bills.map(toRow);
    const byQuick = (r: QueueRow) => {
      switch (quick) {
        case 'requested': return r.kind === 'awaiting';
        case 'unpaid': return r.kind === 'bill' && r.billStatus === 'GENERATED' && (r.paidAmount ?? 0) === 0;
        case 'part-paid': return r.kind === 'bill' && r.billStatus === 'GENERATED' && (r.paidAmount ?? 0) > 0;
        case 'room': return !!r.roomNumber || r.guestPresence === 'IN_HOUSE';
        case 'vip': return r.isVip;
        default: return true;
      }
    };
    return [...a, ...b]
      .filter(byQuick)
      .filter((r) => !q || `${r.billNumber ?? ''} ${r.orderNumber} ${r.tableNumber ?? ''} ${r.roomNumber ?? ''} ${r.guestName ?? ''}`.toLowerCase().includes(q));
  }, [awaiting, bills, searchQuery, filters.status, quick]);

  // The Outlet column only when the queue spans outlets (a shared counter, S1).
  const showOutlet = useMemo(() => new Set([...awaiting, ...bills].map((x) => x.dineOutletId).filter(Boolean)).size > 1, [awaiting, bills]);

  // Settled and Void need other bills from the server; the rest narrow what is already loaded.
  const applyQuick = useCallback((id: string | null) => {
    setQuick(id);
    setFilters((p) => ({ ...p, status: id === 'settled' ? 'settled' : id === 'void' ? 'void' : 'open' }));
  }, []);

  const generate = useCallback(async (orderId: string) => {
    try {
      const bill = await dineOpsService.generateBill(orderId);
      notificationService.success(bill.splitCount > 1 ? `${bill.splitCount} bills generated` : 'Bill generated', bill.splitCount > 1 ? `${bill.billNumber} is share 1 of ${bill.splitCount}; the others are in the queue.` : bill.billNumber);
      await load();
      setSettling(bill.id);
    } catch (err: any) {
      notificationService.error('Not generated', err?.response?.data?.message ?? 'The bill could not be generated.');
    }
  }, [load]);

  const print = useCallback(async (billId: string) => {
    try {
      const p = await dineOpsService.getBillPrint(billId);
      if (!printBill(p)) notificationService.error('Popup blocked', 'Allow popups for this site to print.');
    } catch { notificationService.error('Not printed', 'The invoice could not be loaded.'); }
  }, []);

  const doVoid = useCallback(async () => {
    if (!voiding) return;
    const reason = voidReason.trim();
    if (!reason) { notificationService.error('Reason needed', 'Say why the bill is voided.'); return; }
    try {
      await dineOpsService.voidBill(voiding.id, reason);
      notificationService.success('Voided', `${voiding.billNumber} is void; receipts reversed. The order can be billed again.`);
      setVoiding(null); setVoidReason('');
      void load();
    } catch (err: any) {
      notificationService.error('Not voided', err?.response?.data?.message ?? 'The bill could not be voided.');
    }
  }, [voiding, voidReason, load]);

  const openShift = useCallback(async () => {
    const f = Number(openFloat || '0');
    if (Number.isNaN(f) || f < 0) { notificationService.error('Float', 'Enter the opening float.'); return; }
    try {
      const s = await dineOpsService.openShift(f, openCounter || undefined);
      notificationService.success('Shift opened', `${s.shiftCode} at ${s.billingPointName ?? 'the counter'}. Receipts now go into this drawer.`);
      setOpening(false); setOpenFloat(''); setOpenCounter('');
      void load();
    } catch (err: any) {
      notificationService.error('Shift not opened', err?.response?.data?.message ?? 'The shift could not be opened.');
    }
  }, [openFloat, openCounter, load]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => setNewBill(true), tooltip: 'New bill from an order', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh', variant: 'icon' },
  ], [load]);

  // room / guest / vip use the kitchen display's colours (fixed, so they read the same in both themes).
  const badge = (text: string, tone: 'ok' | 'warn' | 'muted' | 'danger' | 'room' | 'guest' | 'vip') => {
    const bg = { ok: 'var(--color-success-bg, #dcfce7)', warn: 'var(--color-warning-bg, #fef3c7)', muted: 'var(--bg-muted, #f1f5f9)', danger: 'var(--color-danger-bg, #fee2e2)', room: '#6d28d9', guest: '#ede9fe', vip: '#facc15' }[tone];
    const fg = { ok: 'var(--color-success, #166534)', warn: 'var(--color-warning, #92400e)', muted: 'var(--text-secondary, #64748b)', danger: 'var(--color-danger, #991b1b)', room: '#ffffff', guest: '#4c1d95', vip: '#422006' }[tone];
    const strong = tone === 'room' || tone === 'guest' || tone === 'vip';
    return (
      <span data-guest-badge={strong ? tone : undefined}
        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', background: bg, color: fg,
          fontWeight: strong ? 700 : undefined, boxShadow: tone === 'guest' ? 'inset 0 0 0 1px #a78bfa' : undefined }}>
        {text}
      </span>
    );
  };

  const columns: SyutGridColumn<QueueRow>[] = useMemo(() => [
    {
      id: 'billNumber', header: 'Bill', accessorKey: 'billNumber', enableSorting: true, size: 170,
      cell: ({ row }) => (row.original.kind === 'bill' ? <strong>{row.original.billNumber}</strong> : <span style={{ opacity: 0.6 }}>awaiting bill</span>),
    },
    { id: 'orderNumber', header: 'Order', accessorKey: 'orderNumber', enableSorting: true, size: 190, cell: ({ row }) => <span>{row.original.orderNumber}{row.original.shareLabel ? <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 10, background: 'var(--bg-muted, #f1f5f9)' }}>share {row.original.shareLabel}</span> : null}</span> },
    {
      id: 'where', header: 'Table / room', accessorKey: 'tableNumber', enableSorting: true, size: 130,
      cell: ({ row }) => (row.original.roomNumber && !row.original.tableNumber ? `Room ${row.original.roomNumber}` : row.original.tableNumber ?? '—'),
    },
    {
      id: 'guest', header: 'Guest', accessorKey: 'guestPresence', enableSorting: false, size: 260,
      cell: ({ row }) => {
        const r = row.original;
        // Same signals as the kitchen display: room service, a hotel guest at a table, VIP.
        const roomService = !!r.roomNumber && !r.tableNumber;
        const who = roomService ? badge('Room service', 'room')
          : r.guestPresence === 'IN_HOUSE' ? badge(r.roomNumber ? `Hotel guest · Room ${r.roomNumber}` : 'Hotel guest', 'guest')
          : badge('walk-in', 'muted');
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {who}
            {r.isVip ? badge('★ VIP', 'vip') : null}
            {r.guestName ? <span>{r.guestName}</span> : null}
          </span>
        );
      },
    },
    { id: 'outlet', header: 'Outlet', accessorKey: 'outletName', enableSorting: true, size: 140, cell: ({ row }) => row.original.outletName ?? '—' },
    { id: 'counter', header: 'Billing point', accessorKey: 'billingPointName', enableSorting: true, size: 140, cell: ({ row }) => row.original.billingPointName ?? '—' },
    {
      id: 'total', header: 'Amount', accessorKey: 'totalAmount', enableSorting: true, size: 120,
      cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.original.kind === 'bill' ? money(row.original.totalAmount ?? 0) : <span style={{ opacity: 0.7 }}>{money(row.original.subtotal ?? 0)} + tax</span>}</span>,
    },
    {
      id: 'status', header: 'Status', accessorKey: 'billStatus', enableSorting: true, size: 160,
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind === 'awaiting') return r.canBill ? badge(`bill requested · ${r.waitingMinutes ?? 0} min`, 'warn') : badge(r.unservedKots ? 'kitchen not done' : 'unsent lines', 'danger');
        if (r.billStatus === 'SETTLED') return badge(`settled · ${r.settlementType === 'ROOM_CHARGE' ? 'to room' : r.settlementType === 'MIXED' ? 'paid + room' : (r.settlementType ?? '').toLowerCase()}`, 'ok');
        if (r.billStatus === 'VOID') return badge('void', 'danger');
        return badge((r.paidAmount ?? 0) > 0 ? `part paid · due ${money(r.balanceDue ?? 0)}` : 'unpaid', 'warn');
      },
    },
    {
      id: 'time', header: 'Time', accessorKey: 'generatedAt', enableSorting: true, size: 110,
      cell: ({ row }) => {
        const t = row.original.kind === 'bill' ? row.original.generatedAt : row.original.billRequestedAt;
        return t ? new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
      },
    },
  ], []);

  const getRowActions = useCallback((row: QueueRow) => {
    if (row.kind === 'awaiting') {
      return [{ id: 'gen', label: row.canBill ? 'Generate bill' : 'Generate bill (blocked)', icon: <IconSystem.add size={16} />, onClick: () => void generate(row.orderId) }];
    }
    const acts: any[] = [];
    if (row.billStatus === 'GENERATED') acts.push({ id: 'settle', label: 'Settle', icon: <IconSystem.edit size={16} />, onClick: () => setSettling(row.id) });
    else acts.push({ id: 'view', label: 'View', icon: <IconSystem.info size={16} />, onClick: () => setSettling(row.id) });
    acts.push({ id: 'print', label: row.billStatus === 'VOID' ? 'Print (void copy)' : 'Print', icon: <IconSystem.download size={16} />, onClick: () => void print(row.id) });
    if (canMove && row.billStatus === 'GENERATED' && !(row.paidAmount ?? 0))
      acts.push({ id: 'move', label: 'Move to another counter…', icon: <IconSystem.refresh size={16} />, onClick: () => setMoving(bills.find((b) => b.id === row.id) ?? null) });
    if (canVoid && row.billStatus !== 'VOID') acts.push({ id: 'void', label: 'Void…', icon: <IconSystem.trash size={16} />, onClick: () => setVoiding(bills.find((b) => b.id === row.id) ?? null), variant: 'danger' as const });
    return acts;
  }, [generate, print, bills, canMove, canVoid]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Bill Queue"
        description="Tables waiting for a bill, open bills, settlement and print"
        icon={<IconSystem.list size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search bill, order, table or guest' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />
      {/* KOT printing: tickets that did not print, agents offline (BR-PRN-002). A banner only. */}
      <PrintAlertBanner scopeId={currentOutletId()}
        onOpenQueue={onNavigateModule && canSeeModule(PRINT_MODULES.QUEUE) ? () => onNavigateModule(PRINT_MODULES.QUEUE) : undefined} />
      <div className="module-content">
        <div style={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <div data-quick-views style={{ padding: '6px 12px 0', display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, marginRight: '0.25rem' }}>QUICK VIEWS</span>
              {[
                { id: 'requested', label: 'Bill requested', icon: <IconSystem.bell size={12} />, count: awaiting.length },
                { id: 'unpaid', label: 'Unpaid', icon: <IconSystem.clock size={12} />, count: bills.filter((b) => b.billStatus === 'GENERATED' && b.paidAmount === 0).length },
                { id: 'part-paid', label: 'Part paid', icon: <IconSystem.dollarSign size={12} />, count: bills.filter((b) => b.billStatus === 'GENERATED' && b.paidAmount > 0).length },
                { id: 'room', label: 'Room / hotel guests', icon: <IconSystem.home size={12} /> },
                { id: 'vip', label: 'VIP', icon: <IconSystem.star size={12} /> },
                { id: 'settled', label: 'Settled', icon: <IconSystem.checkCircle size={12} /> },
                { id: 'void', label: 'Void', icon: <IconSystem.xCircle size={12} /> },
              ].map((chip) => {
                const active = quick === chip.id;
                return (
                  <button key={chip.id} type="button" data-quick={chip.id} onClick={() => applyQuick(active ? null : chip.id)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.625rem', borderRadius: '999px',
                      fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer',
                      border: active ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                      background: active ? 'var(--primary)' : 'var(--bg-primary)', color: active ? 'white' : 'var(--text-primary)',
                      transition: 'all 0.15s ease',
                    }}>
                    {chip.icon}{chip.label}{chip.count !== undefined && <span style={{ opacity: 0.75 }}>({chip.count})</span>}
                  </button>
                );
              })}
              {quick && (
                <button type="button" onClick={() => applyQuick(null)} title="Clear quick view"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer', border: 'none', background: 'transparent', color: 'var(--text-muted)' }}>
                  <IconSystem.x size={12} /> Clear
                </button>
              )}
            </div>
            <div style={{ padding: '8px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} data-counter-showing>
              <span style={{ opacity: 0.7 }}>
                <strong>{showing}</strong> · {awaiting.length} table(s) waiting for a bill · {bills.filter((b) => b.billStatus === 'GENERATED').length} open bill(s). Refreshes every 15 s.
              </span>
              {!myShift && (
                <button type="button" className="btn btn-primary btn-sm" data-open-shift onClick={() => setOpening(true)}
                  title="Every receipt and refund goes into a cashier's drawer: open a shift at your counter first.">
                  Open a shift…
                </button>
              )}
            </div>
            <SyutGrid
              data={rows}
              columns={showOutlet ? columns : columns.filter((c) => c.id !== 'outlet')}
              enableSorting
              enablePagination
              defaultPageSize={25}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={error ? 'The queue could not be loaded.'
                : noShift ? 'Open a shift at your counter (Shifts screen) to see its bills. Each counter sees only its own.'
                : 'Nothing waiting. A table appears here when the waiter asks for the bill.'}
            />
          </div>
          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}><IconSystem.x size={16} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Bills" options={[{ value: 'open', label: 'Open (and waiting tables)' }, { value: 'settled', label: 'Settled' }, { value: 'void', label: 'Void' }, { value: 'all', label: 'All' }]}
                  value={filters.status || 'open'} onChange={(v) => setFilters((p) => ({ ...p, status: (v as string) || 'open' }))} placeholder="Open" size="sm" />
              </div>
              {canMove && counters.length > 1 && (
                <div className="filter-section">
                  <SyutSelect label="Billing point" options={[
                    { value: '', label: myShift?.billingPointName ? `My counter (${myShift.billingPointName})` : 'My counter' },
                    { value: 'all', label: 'All counters' },
                    ...counters.map((c) => ({ value: c.id, label: c.billingPointName })),
                  ]} value={filters.counter ?? ''} onChange={(v) => setFilters((p) => ({ ...p, counter: (v as string) || '' }))} placeholder="My counter" size="sm" />
                </div>
              )}
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFilters({ status: 'open', counter: '' }); setQuick(null); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {settling && <DineOpsSettleModal isOpen billId={settling} allowedActions={allowedActions} onChanged={() => void load()} onClose={() => { setSettling(null); void load(); }} onPrint={(id) => void print(id)} />}
      <DineOpsMoveBillModal isOpen={!!moving} bill={moving} onClose={() => setMoving(null)} onMoved={() => void load()} />
      {newBill && <DineOpsNewBillModal isOpen awaiting={billable} onGenerate={async (orderId) => { setNewBill(false); await generate(orderId); }} onClose={() => setNewBill(false)} />}

      {opening && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container" style={{ minHeight: 'auto', maxWidth: 480 }} data-open-shift-dialog>
            <div className="modal-header">
              <h2 className="modal-title">Open a shift</h2>
              <button className="modal-close" onClick={() => setOpening(false)}><IconSystem.x size={18} /></button>
            </div>
            <div className="modal-content">
              <p style={{ marginTop: 0, fontSize: 13, opacity: 0.8 }}>
                Receipts and refunds go into your drawer at this counter, for every outlet it serves.
              </p>
              {counters.filter((c) => c.isActive).length > 1 && (
                <div className="form-group">
                  <SyutSelect label="Counter" value={openCounter} size="sm" placeholder="Choose your counter"
                    options={counters.filter((c) => c.isActive).map((c) => ({
                      value: c.id,
                      label: `${c.billingPointName}${c.isShared ? ` (shared, ${c.homeOutletName ?? ''})` : ''}${c.openShiftBy ? ` — shift open: ${c.openShiftBy}` : ''}`,
                      isDisabled: !!c.openShiftBy,
                    }))}
                    onChange={(v) => setOpenCounter((v as string) || '')} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Opening float</label>
                <input className="form-input" type="number" min={0} step="1" value={openFloat} onChange={(e) => setOpenFloat(e.target.value)} placeholder="0" autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setOpening(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void openShift()}>Open shift</button>
            </div>
          </div>
        </>
      )}

      {voiding && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container" style={{ minHeight: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title">Void {voiding.billNumber}?</h2>
              <button className="modal-close" onClick={() => setVoiding(null)}><IconSystem.x size={18} /></button>
            </div>
            <div className="modal-content">
              <p style={{ marginTop: 0, fontSize: 13, opacity: 0.8 }}>
                Receipts on it are reversed, the invoice number stays consumed, and the order can be billed again. A manager's act; a reason is required.
              </p>
              <div className="form-group">
                <label className="form-label">Reason <span className="text-danger">*</span></label>
                <input className="form-input" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g., wrong table" maxLength={200} autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setVoiding(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => void doVoid()}>Void bill</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DineOpsBillQueueScreen;
